import React, { useEffect, useState } from 'react'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  FaArrowLeft,
  FaFileInvoice,
  FaDownload,
  FaTruck,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaWeightHanging,
  FaCheckCircle,
  FaClock,
  FaFileAlt,
  FaChevronRight,
} from 'react-icons/fa'
import {
  fetchBiltyById,
  generateBiltyPDF,
  downloadBiltyPDF,
  approveBilty,
  clearCurrentBilty,
  clearError,
  clearSuccessMessage,
} from '../../store/slices/biltySlice'
import './Bilty.scss'

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' }
  })
}

const BiltyDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const [approveModal, setApproveModal] = useState(false)

  const {
    currentBilty,
    loading,
    error,
    pdfGenerating,
    pdfDownloading,
    approving,
    successMessage,
  } = useSelector(state => state.bilty)

  useEffect(() => {
    if (id) dispatch(fetchBiltyById(id))
    return () => dispatch(clearCurrentBilty())
  }, [dispatch, id])

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => dispatch(clearSuccessMessage()), 3000)
      return () => clearTimeout(t)
    }
  }, [successMessage, dispatch])

  const handleGeneratePDF = () => dispatch(generateBiltyPDF(id))
  const handleDownloadPDF = () => dispatch(downloadBiltyPDF(id))
  const handleApproveBilty = () => {
    dispatch(approveBilty(id))
    setApproveModal(false)
  }

  const formatDate = dateString => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const canApprove = currentBilty?.bilty_type === 'online' && !currentBilty?.transporter_approved

  // ── loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bl-loading" style={{ minHeight: '60vh' }}>
        <Spinner animation='border' size='sm' style={{ color: '#2d3748' }} />
        <span>Loading bilty details…</span>
      </div>
    )
  }

  if (!currentBilty) {
    return (
      <div className="bd-page">
        <div className="bd-alert bd-alert--danger">
          Bilty not found or failed to load.
          <button
            style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
            onClick={() => navigate('/bilty')}
          >Go back to Bilty List</button>
        </div>
      </div>
    )
  }

  const bilty = currentBilty

  return (
    <AnimatePresence>
      <motion.div
        className="bd-page"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >
        {/* ── ALERTS ───────────────────────────────────────────────────── */}
        {error && (
          <div className="bd-alert bd-alert--danger">
            {error}
            <button
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
              onClick={() => dispatch(clearError())}
            >✕</button>
          </div>
        )}
        {successMessage && (
          <div className="bd-alert bd-alert--success">
            <FaCheckCircle style={{ flexShrink: 0 }} />
            {successMessage}
          </div>
        )}

        {/* ── PAGE HEADER ──────────────────────────────────────────────── */}
        <motion.div className="bd-header" custom={0} variants={fade}>
          <button className="bd-back-btn" onClick={() => navigate('/bilty')}>
            <FaArrowLeft />
          </button>
          <div className="bd-header-icon"><FaFileInvoice /></div>
          <div className="bd-header-text">
            <h1 className="bd-title">{bilty.bilty_number}</h1>
            <p className="bd-sub">Bilty / Lorry Receipt Details</p>
          </div>
          <div className="bd-header-actions">
            <span className='bl-badge bl-badge--blue'>{bilty.bilty_type?.toUpperCase()}</span>
            {bilty.transporter_approved
              ? <span className="bl-badge bl-badge--green"><FaCheckCircle className="me-1" />Approved</span>
              : bilty.bilty_type === 'online'
                ? <span className="bl-badge bl-badge--yellow"><FaClock className="me-1" />Pending</span>
                : null
            }
          </div>
        </motion.div>

        {/* ── TWO-COLUMN LAYOUT ────────────────────────────────────────── */}
        <Row className="g-4">

          {/* ── LEFT COL ─────────────────────────────────────────────── */}
          <Col lg={8}>

            {/* Bilty Info */}
            <motion.div className="bd-section" custom={1} variants={fade}>
              <div className="bd-section-title">
                <FaFileInvoice className="bd-section-icon" />
                Bilty Information
              </div>
              <div className="bd-detail-grid">
                <div className="bd-detail-item">
                  <span className="bd-detail-lbl"><FaFileAlt className="me-1" />Bilty Number</span>
                  <span className="bd-detail-val bd-detail-val--accent">{bilty.bilty_number}</span>
                </div>
                <div className="bd-detail-item">
                  <span className="bd-detail-lbl"><FaCalendarAlt className="me-1" />Bilty Date</span>
                  <span className="bd-detail-val">{formatDate(bilty.bilty_date)}</span>
                </div>
                <div className="bd-detail-item">
                  <span className="bd-detail-lbl">Type</span>
                  <span className="bd-detail-val">{bilty.bilty_type}</span>
                </div>
                <div className="bd-detail-item">
                  <span className="bd-detail-lbl">Source</span>
                  <span className="bd-detail-val">
                    {bilty.bilty_source === 'main_portal' ? 'Main Portal' : bilty.bilty_source === 'transporter_portal' ? 'Transporter Portal' : bilty.bilty_source || '—'}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Route */}
            <motion.div className="bd-section" custom={2} variants={fade}>
              <div className="bd-section-title">
                <FaMapMarkerAlt className="bd-section-icon" />
                Route
              </div>
              <div className="bd-route-bar">
                <div className="bd-route-point">
                  <FaMapMarkerAlt className="bd-route-dot bd-route-dot--from" />
                  <span className="bd-route-place">{bilty.origin || '—'}</span>
                </div>
                <FaChevronRight className="bd-route-arrow" />
                <div className="bd-route-point">
                  <FaMapMarkerAlt className="bd-route-dot bd-route-dot--to" />
                  <span className="bd-route-place">{bilty.destination || '—'}</span>
                </div>
              </div>

              {/* Consignor / Consignee */}
              <div className="bd-party-grid">
                <div className="bd-party-item">
                  <span className="bd-party-lbl"><FaMapMarkerAlt style={{ color: '#059669' }} />Consignor</span>
                  <span className="bd-party-name">{bilty.consignor_name || '—'}</span>
                </div>
                <div className="bd-party-item">
                  <span className="bd-party-lbl"><FaMapMarkerAlt style={{ color: '#ef4444' }} />Consignee</span>
                  <span className="bd-party-name">{bilty.consignee_name || '—'}</span>
                </div>
              </div>
            </motion.div>

            {/* Commodity Details */}
            <motion.div className="bd-section" custom={3} variants={fade}>
              <div className="bd-section-title">
                <FaWeightHanging className="bd-section-icon" />
                Commodity Details
              </div>
              <div className="bd-table-wrap">
                <table className="bd-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Unit</th>
                      <th>Rate/Unit</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilty.commodityDetails?.length > 0 ? (
                      bilty.commodityDetails.map(item => (
                        <tr key={item.id}>
                          <td className="bd-table-num">{item.sr_no}</td>
                          <td className="bd-table-bold">{item.commodity_name}</td>
                          <td>{item.quantity}</td>
                          <td><span className="bl-badge bl-badge--gray">{item.quantity_unit}</span></td>
                          <td className="bd-table-num">₹{Number(item.rate_per_unit || 0).toLocaleString()}</td>
                          <td className="bd-table-num" style={{ color: '#1a202c', fontWeight: 700 }}>₹{Number(item.amount || 0).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', color: '#a0aec0', padding: '20px' }}>No commodity items</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Freight Charges */}
            <motion.div className="bd-section" custom={4} variants={fade}>
              <div className="bd-section-title">
                <FaFileAlt className="bd-section-icon" />
                Freight Charges
              </div>
              <div className="bd-freight-grid">
                <div className="bd-freight-item">
                  <span className="bd-freight-lbl">Total Freight</span>
                  <span className="bd-freight-val bd-freight-val--blue">₹{Number(bilty.total_freight || 0).toLocaleString()}</span>
                </div>
                <div className="bd-freight-item">
                  <span className="bd-freight-lbl">Advance</span>
                  <span className="bd-freight-val bd-freight-val--green">₹{Number(bilty.advance_amount || 0).toLocaleString()}</span>
                </div>
                <div className="bd-freight-item">
                  <span className="bd-freight-lbl">To Pay</span>
                  <span className="bd-freight-val bd-freight-val--red">₹{Number(bilty.to_pay_amount || 0).toLocaleString()}</span>
                </div>
              </div>
            </motion.div>

          </Col>

          {/* ── SIDEBAR ────────────────────────────────────────────────── */}
          <Col lg={4}>
            <motion.div className="bd-sidebar" custom={5} variants={fade}>

              {/* Vehicle & Driver */}
              <div className="bd-sidebar-card">
                <div className="bd-sidebar-card-title">Vehicle & Driver</div>
                <div className="bd-entity-card">
                  <div className="bd-entity-icon"><FaTruck /></div>
                  <div className="bd-entity-info">
                    <span className="bd-entity-lbl">Vehicle</span>
                    <span className="bd-entity-name">{bilty.truck_number || '—'}</span>
                  </div>
                </div>
                {bilty.driver_name && (
                  <div className="bd-entity-card">
                    <div className="bd-entity-icon"><FaUser /></div>
                    <div className="bd-entity-info">
                      <span className="bd-entity-lbl">Driver</span>
                      <span className="bd-entity-name">{bilty.driver_name}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="bd-sidebar-card">
                <div className="bd-sidebar-card-title">Actions</div>

                {canApprove && (
                  <button
                    className="bd-approve-btn"
                    onClick={() => setApproveModal(true)}
                    disabled={approving}
                  >
                    {approving
                      ? <><Spinner animation="border" size="sm" className="me-2" />Approving…</>
                      : <><FaCheckCircle className="me-2" />Approve Bilty</>
                    }
                  </button>
                )}

                {!bilty.pdf_url ? (
                  <button
                    className="bd-pdf-btn"
                    onClick={handleGeneratePDF}
                    disabled={pdfGenerating}
                    style={{ marginTop: canApprove ? 0 : 10 }}
                  >
                    {pdfGenerating
                      ? <><Spinner animation="border" size="sm" className="me-2" />Generating…</>
                      : <><FaFileAlt className="me-2" />Generate PDF</>
                    }
                  </button>
                ) : (
                  <button
                    className="bd-pdf-btn"
                    onClick={handleDownloadPDF}
                    disabled={pdfDownloading}
                    style={{ marginTop: canApprove ? 0 : 10 }}
                  >
                    {pdfDownloading
                      ? <><Spinner animation="border" size="sm" className="me-2" />Downloading…</>
                      : <><FaDownload className="me-2" />Download PDF</>
                    }
                  </button>
                )}
              </div>

              {/* Approval Info */}
              {bilty.transporter_approved && bilty.approved_at && (
                <div className="bd-sidebar-card">
                  <div className="bd-sidebar-card-title">
                    <FaCheckCircle className="me-2" style={{ color: '#059669' }} />
                    Approval Details
                  </div>
                  <div className="bd-approved-row">
                    <div className="bl-badge bl-badge--green" style={{ alignSelf: 'flex-start' }}>Approved</div>
                    <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>
                      <span style={{ fontWeight: 700, display: 'block', color: '#2d3748' }}>{formatDate(bilty.approved_at)}</span>
                      {bilty.approved_by && <span style={{ color: '#718096' }}>By: {bilty.approved_by}</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* PDF Info */}
              {bilty.pdf_url && (
                <div className="bd-sidebar-card">
                  <div className="bd-sidebar-card-title">
                    <FaFileAlt className="me-2" style={{ color: '#718096' }} />
                    PDF Document
                  </div>
                  <div className="bd-approved-row">
                    <div style={{ fontSize: '0.78rem', color: '#718096' }}>
                      Generated on
                      <span style={{ fontWeight: 700, color: '#2d3748', display: 'block' }}>{formatDate(bilty.pdf_generated_at)}</span>
                    </div>
                    <button
                      className="bd-pdf-btn"
                      onClick={handleDownloadPDF}
                      disabled={pdfDownloading}
                      style={{ width: '100%', margin: 0 }}
                    >
                      <FaDownload className="me-2" />Download PDF
                    </button>
                  </div>
                </div>
              )}

            </motion.div>
          </Col>
        </Row>

        {/* ── APPROVE MODAL ────────────────────────────────────────────── */}
        <Modal
          show={approveModal}
          onHide={() => setApproveModal(false)}
          centered
          size="sm"
        >
          <Modal.Header closeButton className="bd-modal-header">
            <Modal.Title className="bd-modal-title">Approve Bilty</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bd-modal-body">
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <FaCheckCircle style={{ fontSize: '2.8rem', color: '#059669', marginBottom: 12 }} />
              <h6 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, marginBottom: 6 }}>Confirm Approval</h6>
              <p style={{ fontSize: '0.83rem', color: '#718096', marginBottom: 14 }}>
                Are you sure you want to approve bilty<br />
                <strong style={{ color: '#2d3748' }}>{bilty.bilty_number}</strong>?
              </p>
              <div className="bd-modal-note">
                <strong>Note:</strong> Once approved, you acknowledge that you have received this bilty from the Main Portal and accept all details as accurate.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="bd-modal-footer">
            <button className="bd-btn-cancel" onClick={() => setApproveModal(false)}>
              Cancel
            </button>
            <button
              className="bd-btn-approve"
              onClick={handleApproveBilty}
              disabled={approving}
            >
              {approving
                ? <><Spinner animation="border" size="sm" className="me-2" />Approving…</>
                : <><FaCheckCircle className="me-1" />Confirm Approval</>
              }
            </button>
          </Modal.Footer>
        </Modal>

      </motion.div>
    </AnimatePresence>
  )
}

export default BiltyDetails
